import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Wallet, AlertTriangle, ShieldAlert, Clock, Download, Search, FileDown, Upload } from '@/components/icons';
import { ExcelImportModal } from '@/components/ui/ExcelImportModal';
import { downloadExcel } from '@/lib/downloadExcel';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { KpiCard } from '@/components/ui/KpiCard';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { PeriodPicker, REPORT_UNITS, usePeriod } from '@/components/ui/PeriodPicker';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { formatINR } from '@/lib/format';
import { customerOsTemplatePath, getCustomerOs, importCustomerOs } from '@/api/accounts';
import { OsAgeingTable } from './OsAgeingTable';

export function CustomerOsPage() {
  // Invoice date — All / Date (from–to) / Month / Quarter / Year; aged as at the period's end.
  const period = usePeriod();
  const { range } = period;
  const [customer, setCustomer] = useState('');
  const [search, setSearch] = useState('');
  const [exportTick, setExportTick] = useState(0);
  const [importOpen, setImportOpen] = useState(false);
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['accounts', 'customer-os', range?.from ?? '', range?.to ?? ''],
    queryFn: () => getCustomerOs({ from: range?.from, to: range?.to }),
  });
  const data = query.data;
  const k = data?.kpis;
  const groups = useMemo(() => data?.groups ?? [], [data]);
  const totals = data?.totals ?? { d0: 0, d1: 0, d2: 0, d3: 0, total: 0 };

  return (
    <div>
      <SectionHeader
        title="Customer Outstanding"      />

      <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiCard label="Total Outstanding" value={k ? `₹${formatINR(k.totalOutstanding)}` : '—'} accent="#FF8C00" Icon={Wallet} loading={query.isLoading} />
        <KpiCard label="Overdue 90+ days" value={k ? `₹${formatINR(k.overdue90)}` : '—'} accent="#f0576a" Icon={AlertTriangle} loading={query.isLoading} />
        <KpiCard label="Over Credit Limit" value={k ? k.overCreditLimit : '—'} accent="#f0a92b" Icon={ShieldAlert} loading={query.isLoading} />
        <KpiCard label="Avg Days Outstanding" value={k ? k.avgDays : '—'} accent="#5b8def" Icon={Clock} loading={query.isLoading} />
      </div>

      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto sm:min-w-[220px]">
          <div className="relative w-full">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              title="Search by customer, code or invoice"
              className="!pl-5 sm:!pl-8"
              aria-label="Search customer outstanding"
            />
          </div>
        </FilterField>
        <FilterField label="Customer">
          <Select value={customer} onChange={(e) => setCustomer(e.target.value)}>
            <option value="">All customers</option>
            {groups.map((g) => <option key={g.code} value={g.name}>{g.name}</option>)}
          </Select>
        </FilterField>
        <FilterField label="Period" className="col-span-3 sm:col-auto">
          <PeriodPicker {...period.picker} units={REPORT_UNITS} className="justify-start" />
        </FilterField>
        <div className="col-span-3 flex flex-nowrap gap-1.5 [&>*]:flex-auto sm:col-span-1 sm:ml-auto sm:self-end sm:[&>*]:flex-none">
          <button
            type="button"
            title="Export to Excel — every row matching these filters and search"
            onClick={() => setExportTick((t) => t + 1)}
            className="inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:h-8 sm:gap-1.5 sm:px-3 sm:text-xs"
          >
            <Download size={12} /> Export
          </button>
          <button
            type="button"
            title="Download the upload sheet with the correct columns and one example row"
            onClick={() => downloadExcel(customerOsTemplatePath, 'GRHAMOY_Customer_Outstanding_Template.xlsx')}
            className="inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:h-8 sm:gap-1.5 sm:px-3 sm:text-xs"
          >
            <FileDown size={12} /> Template
          </button>
          <button
            type="button"
            title="Upload customer outstanding bills from Excel"
            onClick={() => setImportOpen(true)}
            className="inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:h-8 sm:gap-1.5 sm:px-3 sm:text-xs"
          >
            <Upload size={12} /> Import
          </button>
        </div>
      </FilterBar>

      <OsAgeingTable
        groups={groups}
        totals={totals}
        partyLabel="Customer"
        filterParty={customer}
        search={search}
        exportSignal={exportTick}
        exportName="Customer_Outstanding"
      />

      <ExcelImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Customer Outstanding"
        expectedColumns={['Customer Code*', 'Customer Name', 'Invoice No*', 'Invoice Date*', 'Outstanding Amount*']}
        columnDetails={[
          { name: 'Customer Code', hint: 'Code as in Party Master — rows with an unknown code are skipped', example: 'C0001' },
          { name: 'Customer Name', hint: 'For your reference; the code decides the customer', example: 'Sharma Interiors', optional: true },
          { name: 'Invoice No', hint: 'Same invoice no. uploaded again updates that bill', example: 'INV/24-25/0153' },
          { name: 'Invoice Date', hint: 'DD-MM-YYYY (or an Excel date); ageing counts from this', example: '15-03-2025' },
          { name: 'Outstanding Amount', hint: 'Amount still due in ₹; 0 removes a bill uploaded earlier', example: '48500' },
        ]}
        onImport={importCustomerOs}
        onSuccess={() => qc.invalidateQueries({ queryKey: ['accounts', 'customer-os'] })}
      />
    </div>
  );
}
